import { Group, Paper, Stack, Text } from '@mantine/core';
import { Sparkles } from 'lucide-react';
import { resolveSourceLabels } from './sourceLabels';

/**
 * MED-876 AC2 — footer row of the per-step AI Suggestions section.
 *
 * Purple-tinted background row with a sparkle icon prefix, the synthesized
 * AI note text, and a row of source labels below.
 *
 * MED-1031 — the source labels are PROVENANCE, not actions: they show what
 * context the AI note was grounded in (e.g. "session 6", "no prior feedback").
 * Several have no meaningful click target (session anatomy, session number), so
 * per the coach-approved resolution they are rendered as plainly
 * non-interactive labels — a flat "Based on" caption with outlined, default-
 * cursor tokens — rather than the filled, rounded chips that read as tappable
 * buttons and did nothing on click. Tokens also wrap on narrow viewports.
 *
 * MED-1065 — the recipe emits machine-ish source traces ("Jordan's primary_goal",
 * "session_anatomy pack §3", "holding_space pack §2.4"). The render layer maps
 * them to display chips ON PURPOSE: the backend — and its safety judge, which
 * verifies every source traces to a real input field — keeps the precise raw
 * trace; only the displayed chip is remapped.
 *
 * MED-1195 — {@link resolveSourceLabels} maps each trace to Brook & Ronnie's
 * client-approved category label ("Participant Matching Form", "Your Prep Notes").
 * Traces outside that approved set are DROPPED (the row is a controlled vocabulary,
 * not the AI's raw wording), so this footer may show fewer chips — or none, in
 * which case the "Based on" row is hidden. See sourceLabels.ts.
 */
export interface AINoteFooterProps {
  text: string;
  sources: string[];
}

export function AINoteFooter({ text, sources }: AINoteFooterProps) {
  const labels = resolveSourceLabels(sources);
  return (
    <Paper
      radius="sm"
      p="sm"
      style={{ backgroundColor: '#f3f0ff', borderLeft: '3px solid #b794ff' }}
    >
      <Stack gap={6}>
        <Group gap={6} align="flex-start" wrap="nowrap">
          <Sparkles
            size={14}
            color="#7c4dff"
            style={{ marginTop: 2, flexShrink: 0 }}
          />
          <Text size="sm" c="#403564" style={{ lineHeight: 1.5 }}>
            <Text span fw={600} c="#5a3eb6">
              AI note:
            </Text>{' '}
            {text}
          </Text>
        </Group>
        {labels.length > 0 && (
          <Group gap={6} wrap="wrap" align="center" pl={20}>
            <Text
              size="xs"
              fw={600}
              c="#8a7cb8"
              tt="uppercase"
              style={{ letterSpacing: 0.3, cursor: 'default' }}
            >
              Based on
            </Text>
            {labels.map((label, idx) => (
              <Text
                key={idx}
                size="xs"
                c="#6b5e94"
                // Provenance label — explicitly NOT interactive (MED-1031).
                // Outlined + flat (not a filled chip), squared corners, default
                // cursor: reads as metadata, not a button. No nowrap so a long
                // source (e.g. the full participant breakdown) wraps on narrow
                // viewports instead of overflowing.
                style={{
                  backgroundColor: 'transparent',
                  border: '1px solid #d9ccf5',
                  padding: '1px 8px',
                  borderRadius: 4,
                  cursor: 'default',
                }}
              >
                {label}
              </Text>
            ))}
          </Group>
        )}
      </Stack>
    </Paper>
  );
}

export default AINoteFooter;
